import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { Header, Body } from "../styleguide/atoms/index.js";
import DeleteData from "./game/utils/DeleteData.js";
import * as styles from "../styleguide/styles/Stylesheet.js";

const PageShell = styled.div`
  width: 100%;
  background: ${styles.color_white};
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Container = styled.div`
  width: 100%;
  max-width: ${styles.width_md};
  padding: 64px ${styles.spacing_standard} 96px;
`;

const Section = styled.section`
  margin: 32px 0;
`;

const Small = styled.div`
  font-family: ${styles.font_sans};
  font-size: 13px;
  color: ${styles.color_gry_4};
`;

const List = styled.ul`
  padding-left: 1.25rem;
  margin: 8px 0 0;
  li {
    margin: 6px 0;
  }
`;

const LinkA = styled.a`
  color: ${styles.color_primary};
  text-decoration: underline;
`;

const NavLinkWrapper = styled.div`
  padding: 0 0 ${styles.spacing_standard};
`;

const NavLink = styled(Link)`
  color: ${styles.color_accent};
  font-family: ${styles.font_sans};
  font-size: 16px;
  transition: all 250ms;

  &:hover {
    color: ${styles.color_accent_dark};
  }
`;

export default function PrivacyPolicy() {
  const LAST_UPDATED = "November 29, 2025";
  const contactEmail = "summercampslashergame@gmail.com";

  const handleNavClick = (e) => {
    e.stopPropagation?.();
  };

  return (
    <PageShell>
      <Container>
        <NavLinkWrapper>
          <NavLink to="/" onClick={handleNavClick}>
            Go Home
          </NavLink>
        </NavLinkWrapper>
        <Header type="h1" padding={`0 0 ${styles.spacing_standard_half}`}>
          Privacy Policy
        </Header>
        <Small>Last updated: {LAST_UPDATED}</Small>

        <Section>
          <Body>
            Summer Camp Slasher (“we,” “us,” “our”) operates{" "}
            <strong>summercampslasher.com</strong> and{" "}
            <strong>stage.summercampslasher.com</strong>. This policy explains
            what we collect, how we use it, and your choices. If you have
            questions, contact us at{" "}
            <LinkA href={`mailto:${contactEmail}`}>{contactEmail}</LinkA>.
          </Body>
        </Section>

        <Section>
          <Header type="h2">What we collect</Header>
          <Body>
            We collect the minimum information needed to run the game and
            improve performance:
            <List>
              <li>
                <strong>Authentication data</strong> — We use{" "}
                <em>Firebase Authentication (anonymous sign-in)</em>. Firebase
                issues an anonymous identifier for your session/account.
              </li>
              <li>
                <strong>Gameplay data</strong> — Game/lobby IDs, character
                selections, votes, in-game state (including villain chat, public
                chat, and dead chat), and timestamps required to operate a
                match.
              </li>
              <li>
                <strong>Service logs & device info</strong> — Standard logs from
                our hosting and database (IP address, user agent, pages
                requested, timestamps). These help with security and debugging.
              </li>
              <li>
                <strong>Content delivery</strong> — We load images/text from{" "}
                <em>Contentful</em> (character art and descriptions). Contentful
                may log standard request metadata (e.g., IP, user agent).
              </li>
              <li>
                <strong>Analytics (production only)</strong> — We may enable{" "}
                Firebase/Google Analytics on <em>summercampslasher.com</em> to
                understand usage and improve features.
              </li>
            </List>
            We don’t intentionally collect names/emails unless you directly
            provide them (e.g., a nickname entered in-game or when contacting
            support).
          </Body>
        </Section>

        <Section>
          <Header type="h2">How we use data</Header>
          <Body>
            <List>
              <li>Operate and secure the game and back-end services.</li>
              <li>Fix bugs, prevent abuse, and understand feature usage.</li>
              <li>Communicate service notices or important changes.</li>
            </List>
            We <strong>do not collect or sell</strong> your personal
            information.
          </Body>
        </Section>

        <Section>
          <Header type="h2">Cookies and local storage</Header>
          <Body>
            Firebase/Auth and our front end may use cookies or local storage to
            keep you signed in, remember preferences, and ensure real-time game
            functionality. If Analytics is enabled in production, it may set
            additional cookies.
            <br />
            <br />
            Third-party payment providers used for tips may also set their own
            cookies or similar technologies when you interact with their widgets
            or pages. Where required by law, we will gate non-essential cookies
            behind consent controls. Where required by law, we will gate
            non-essential cookies behind consent controls.
          </Body>
        </Section>

        <Section>
          <Header type="h2">Third-party services we use</Header>
          <Body>
            We use reputable providers to run the game:
            <List>
              <li>
                <strong>Firebase (Google Cloud)</strong> — Auth (anonymous),
                Firestore, Cloud Functions, Hosting (and optional Analytics).
              </li>
              <li>
                <strong>Contentful</strong> — Delivery of images and text for
                characters/content.
              </li>
              <li>
                <strong>Payment / tipping provider</strong> — We may use a
                third-party service (Stripe, Buy Me a Coffee, Venmo) to process
                voluntary supporter payments or tips. Their privacy policy and
                terms apply to those transactions.
              </li>
            </List>
            Each provider processes data under its own terms and privacy policy.
          </Body>
        </Section>

        <Section>
          <Header type="h2">Supporter payments / tips</Header>
          <Body>
            If you choose to send a tip or other voluntary payment, the
            transaction is processed by a third-party payment provider (Stripe,
            Buy Me a Coffee, Venmo). We don’t see your full card number, but we
            may receive limited information such as the amount, timestamp, and
            (if you provide it) your name or email.
          </Body>
        </Section>

        <Section>
          <Header type="h2">Legal bases (EEA/UK visitors)</Header>
          <Body>
            Where applicable, we rely on: (i) performance of a contract (provide
            the game), (ii) legitimate interests (security, diagnostics, basic
            analytics), and (iii) consent (for non-essential analytics cookies,
            if enabled). You can withdraw consent via the site’s cookie/consent
            controls if provided.
          </Body>
        </Section>

        <Section>
          <Header type="h2">Your rights</Header>
          <Body>
            Depending on your location, you may have rights to access, correct,
            delete, or restrict processing of your personal data, and to object
            or withdraw consent. Contact{" "}
            <LinkA href={`mailto:${contactEmail}`}>{contactEmail}</LinkA> to
            exercise rights. Residents of certain U.S. states (e.g., California)
            may have additional rights. If we ever engage in cross-context
            behavioral advertising, we will provide a “Do Not Sell or Share My
            Personal Information” link.
          </Body>
        </Section>

        <Section>
          <Header type="h2">Data retention</Header>
          <Body>
            We retain gameplay data and logs for the time needed to operate and
            troubleshoot the game. We periodically clean up old/expired lobbies
            and sessions (and may use Firestore TTL where configured). If you
            request deletion and it applies, we’ll delete or de-identify data
            where feasible.
          </Body>
        </Section>

        <Section>
          <Header type="h2">Children’s privacy</Header>
          <Body>
            The game is not directed to children under 13. If you believe a
            child provided personal information, contact us and we will delete
            it.
          </Body>
        </Section>

        <Section>
          <Header type="h2">Security</Header>
          <Body>
            We use reasonable safeguards (encryption in transit, access
            controls). No method of transmission or storage is 100% secure.
          </Body>
        </Section>

        <Section>
          <Header type="h2">International transfers</Header>
          <Body>
            We and our providers may process data in countries other than yours.
            Where required, appropriate safeguards are used.
          </Body>
        </Section>

        <Section>
          <Header type="h2">Changes to this policy</Header>
          <Body>
            We may update this policy to reflect changes to the game or legal
            requirements. We will revise the “Last updated” date above and post
            the new version at this URL.
          </Body>
        </Section>

        <Section>
          <Header type="h2">Contact</Header>
          <Body>
            Questions or requests:{" "}
            <LinkA href={`mailto:${contactEmail}`}>{contactEmail}</LinkA>
          </Body>
        </Section>
        <DeleteData />

        <Section>
          <Small>
            <em>Note:</em> This page is informational and not legal advice.
          </Small>
        </Section>
      </Container>
    </PageShell>
  );
}
